/**
 * Die Beschriftungen der App-Editoren übersetzen.
 *
 * **Anders als die Texte des Panels.** Label und Hinweise eines App-Editors
 * stehen in der Konfiguration der App (editor_config, info_editor, den
 * Editor-Versionen), nicht in lang/*\/ui. Das Wörterbuch unter
 * lang/{sprache}/app_editors/*.json übersetzt sie nach dem deutschen Text:
 * `{"Titel": "Title"}`. Die Konfiguration der Apps bleibt unberührt, es
 * entsteht keine neue Editor-Version.
 *
 * **Zahlen sind Platzhalter.** "Das Bild ist 500x500 Pixel groß" und
 * "… 474x474 …" sind derselbe Eintrag: `Das Bild ist {0}x{1} Pixel groß`.
 * Gegenstück in PHP: App\Support\AppEditorTexts::normalize().
 *
 * Fehlt ein Eintrag, bleibt der Text, wie er ist. Schlechter als bisher
 * wird es dadurch nie.
 *
 * Bewusst ohne Vue und Browser, damit es der Testläufer prüfen kann.
 *
 * @see lang/README.md
 */

const NUMBER = /\d+(?:[.,]\d+)?/g;

export function normalizeAppText(text) {
    const numbers = [];
    const template = String(text).trim().replace(NUMBER, (match) => `{${numbers.push(match) - 1}}`);

    return {template, numbers};
}

const has = (dictionary, key) => Object.prototype.hasOwnProperty.call(dictionary, key);

/**
 * Die Übersetzung eines Textes, oder undefined, wenn das Wörterbuch ihn
 * nicht kennt. Erst der exakte Text, dann die Vorlage mit den Zahlen des
 * Originals.
 */
export function lookupAppText(dictionary, text) {
    if (!dictionary || typeof dictionary !== 'object' || typeof text !== 'string' || text.trim() === '') return undefined;

    const trimmed = text.trim();
    if (has(dictionary, trimmed) && typeof dictionary[trimmed] === 'string') return dictionary[trimmed];

    const {template, numbers} = normalizeAppText(trimmed);
    if (numbers.length && has(dictionary, template) && typeof dictionary[template] === 'string') {
        return dictionary[template].replace(/\{(\d+)\}/g, (placeholder, index) => numbers[Number(index)] ?? placeholder);
    }

    return undefined;
}

export function translateAppText(dictionary, text) {
    return lookupAppText(dictionary, text) ?? text;
}

/**
 * Mehrere Wörterbücher, das erste mit Treffer gewinnt.
 *
 * So steht die eigene Übersetzung einer App vor dem gemeinsamen Wörterbuch:
 * `translateAppTextFrom([app.editor_translations.en_gb, dictionary], text)`.
 */
export function translateAppTextFrom(dictionaries, text) {
    for (const dictionary of dictionaries) {
        const found = lookupAppText(dictionary, text);
        if (found !== undefined) return found;
    }

    return text;
}

/**
 * Ein Eintrag aus der Editor-Definition mit übersetzten Beschriftungen.
 *
 * Eine flache Kopie: Die Definition im Store bleibt, wie sie ist -- sie
 * wird gespeichert und verglichen. Übersetzt wird nur, was ein Mensch
 * liest: label, hint, dieselben Angaben in vuetify_config, und bei
 * Auswahlfeldern die Anzeige der Optionen. Der gespeicherte Wert einer
 * Option bleibt der deutsche Text, sonst änderte der Sprachwechsel den
 * Inhalt.
 *
 * @param {object} item  Eintrag aus editor_config/info_editor
 * @param {(text: string) => string} translate
 */
export function localizeEditorItem(item, translate) {
    if (!item || typeof item !== 'object') return item;

    const localized = {...item};
    for (const key of ['label', 'hint']) {
        if (typeof item[key] === 'string') localized[key] = translate(item[key]);
    }

    const config = item.vuetify_config;
    if (config && typeof config === 'object' && !Array.isArray(config)) {
        const localizedConfig = {...config};
        for (const key of ['label', 'hint', 'placeholder']) {
            if (typeof config[key] === 'string') localizedConfig[key] = translate(config[key]);
        }

        // Nur Auswahlfelder: In einer Combobox wären Objekte eingegebene Werte.
        if (item.component === 'SelectField' && Array.isArray(config.items)) {
            localizedConfig.items = config.items.map((option) => {
                if (typeof option === 'string') return {title: translate(option), value: option};
                if (option && typeof option === 'object' && typeof option.title === 'string') {
                    return {...option, title: translate(option.title)};
                }
                return option;
            });
        }

        localized.vuetify_config = localizedConfig;
    }

    return localized;
}

// ----------------------------------------------------------------------------
// Eigene Übersetzungen einer App
//
// Wo das gemeinsame Wörterbuch für eine App nicht passt oder einen Text nicht
// kennt, trägt die App eine eigene Übersetzung (applications.
// editor_translations): `{"en_gb": {"Titel": "Heading"}}`. Schlüssel ist die
// Vorlage wie im Wörterbuch, Zahlen als {0}, {1}. Sie gilt nur für diese App
// und hat Vorrang vor dem Wörterbuch. Gepflegt auf der Seite der App unter
// Applications.
//
// @see \App\Support\AppEditorTexts::cleanTranslations()
// ----------------------------------------------------------------------------

const LETTERS = /\p{L}{2,}/u;

/**
 * Alle Texte einer oder mehrerer Editor-Definitionen, die
 * localizeEditorItem() übersetzt, als Vorlage und in der Reihenfolge ihres
 * ersten Auftretens.
 *
 * Liest dieselben Stellen wie App\Support\AppEditorTexts::collect(). Texte
 * ohne zwei Buchstaben am Stück ("{0}", "–") bleiben draußen, da gibt es
 * nichts zu übersetzen.
 *
 * @returns {Array<{template: string, example: string, count: number}>}
 */
export function collectEditorTexts(...definitions) {
    const texts = new Map();

    const add = (text) => {
        if (typeof text !== 'string' || text.trim() === '' || !LETTERS.test(text)) return;

        const {template} = normalizeAppText(text);
        const entry = texts.get(template) ?? {template, example: text.trim(), count: 0};
        entry.count++;
        texts.set(template, entry);
    };

    const walk = (node) => {
        if (!node || typeof node !== 'object') return;

        // Wie isset() in PHP: null zählt nicht
        if (!Array.isArray(node) && (node.component != null || node.label != null || node.hint != null)) {
            add(node.label);
            add(node.hint);

            const config = node.vuetify_config;
            if (config && typeof config === 'object' && !Array.isArray(config)) {
                add(config.label);
                add(config.hint);
                add(config.placeholder);

                if (node.component === 'SelectField' && Array.isArray(config.items)) {
                    for (const option of config.items) {
                        add(typeof option === 'string' ? option : option?.title);
                    }
                }
            }
        }

        for (const [key, child] of Object.entries(node)) {
            if (key !== 'vuetify_config' && key !== 'component_config' && child && typeof child === 'object') {
                walk(child);
            }
        }
    };

    for (const definition of definitions) {
        walk(typeof definition === 'string' ? safeParse(definition) : definition);
    }

    return [...texts.values()];
}

function safeParse(value) {
    try {
        return JSON.parse(value);
    } catch (e) {
        return null;
    }
}

/** Die Platzhalter eines Textes, sortiert: "{0}x{1}" ergibt "{0},{1}". */
export function placeholdersOf(text) {
    return (String(text).match(/\{\d+\}/g) ?? []).sort().join(',');
}

/**
 * Eigene Übersetzungen in der Form, in der sie gespeichert werden: nur
 * bekannte Sprachen, Werte getrimmt, Leeres weg. Nichts übrig ergibt `{}`.
 *
 * Gegenstück in PHP: App\Support\AppEditorTexts::cleanTranslations().
 *
 * @param {unknown} value
 * @param {string[]} locales  die Sprachen, für die es eigene Übersetzungen gibt
 */
export function cleanEditorTranslations(value, locales) {
    const clean = {};
    if (!value || typeof value !== 'object' || Array.isArray(value)) return clean;

    for (const locale of locales) {
        const entries = value[locale];
        if (!entries || typeof entries !== 'object' || Array.isArray(entries)) continue;

        const kept = {};
        for (const [key, text] of Object.entries(entries)) {
            if (typeof text !== 'string' || text.trim() === '' || key.trim() === '') continue;
            kept[key.trim()] = text.trim();
        }

        if (Object.keys(kept).length) clean[locale] = kept;
    }

    return clean;
}
